import styled, { css } from 'styled-components';

import {
  BodyLargeMedium,
  BodySmallMedium,
  BodySmallSemiBold,
  BodyStandardMedium,
  BodyStandardSemiBold,
  Colour,
  HeadingSmallSemiBold,
  withAlpha,
} from '@xemplo/style-constants';

import { ButtonSize, ButtonType, ButtonVariant } from './button.types';

const DefaultButtonStyles = css`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  border: none;
  padding: 8px 16px 8px 16px;
  border-radius: 24px;
  transition: all 0.1s ease-out;

  &[data-size=${ButtonSize.Small}] {
    ${BodySmallSemiBold};
    height: 32px;
  }
  &[data-size=${ButtonSize.Medium}] {
    ${BodyStandardSemiBold}
    height: 40px;
  }
  &[data-size=${ButtonSize.Standard}] {
    ${BodyStandardSemiBold}
    height: 48px;
    padding: 12px 24px 12px 24px;
  }
  &[data-size=${ButtonSize.Large}] {
    ${HeadingSmallSemiBold}
    height: 64px;
    padding: 20px 24px 20px 24px;
    border-radius: 32px;
  }

  &[disabled] {
    pointer-events: none;
    opacity: 0.3;
  }
`;

export const StyledChildrenContainer = styled.div`
  padding: 0 4px 0 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const StyledButton = styled.button`
  ${DefaultButtonStyles};

  &[data-type=${ButtonType.Primary}] {
    background: ${Colour.Clarity.Blue.Hard};
    color: ${Colour.Clarity.Base.White};
    &:hover {
      background: ${withAlpha(Colour.Clarity.Blue.Hard, 0.8)};
    }
  }
  &[data-type=${ButtonType.Secondary}] {
    &[data-variant=${ButtonVariant.Light}] {
      background: none;
      color: ${Colour.Clarity.Base.White};
      border: 1px solid ${Colour.Clarity.Base.White};
      &:hover {
        background: ${withAlpha(Colour.Clarity.Base.White, 0.1)};
      }
    }
    &[data-variant=${ButtonVariant.Dark}] {
      background: none;
      color: ${Colour.Clarity.Content.Soft};
      border: 1px solid ${Colour.Clarity.Grey.Soft};
      &:hover {
        background: ${withAlpha(Colour.Clarity.Grey.Hard, 0.1)};
        border: 1px solid ${Colour.Clarity.Grey.Medium};
        color: ${Colour.Clarity.Content.Medium};
      }
    }
  }

  &[data-type=${ButtonType.Destructive}] {
    background: ${Colour.Clarity.Red.Hard};
    color: ${Colour.Clarity.Base.White};
    &:hover {
      background: ${withAlpha(Colour.Clarity.Red.Hard, 0.8)};
    }
  }

  &[data-type=${ButtonType.Tertiary}] {
    background: none;
    padding: 0px;
    gap: 6px;
    & > div {
      padding: 0px;
    }
    &[data-underline='true'] {
      text-decoration: underline;
    }

    ${BodyStandardMedium};
    &[data-size=${ButtonSize.Small}] {
      ${BodySmallMedium};
      gap: 4px;
    }
    &[data-size=${ButtonSize.Large}] {
      ${BodyLargeMedium};
      gap: 8px;
    }
    &[data-variant=${ButtonVariant.Light}] {
      color: ${withAlpha(Colour.Clarity.Base.White, 0.8)};
      &:hover {
        color: ${Colour.Clarity.Base.White};
      }
    }
    &[data-variant=${ButtonVariant.Dark}] {
      color: ${Colour.Clarity.Content.Soft};
      &:hover {
        color: ${withAlpha(Colour.Clarity.Content.Soft, 0.8)};
      }
    }
    &[data-variant=${ButtonVariant.Error}] {
      color: ${Colour.Clarity.Red.Hard};
      &:hover {
        color: ${withAlpha(Colour.Clarity.Red.Hard, 0.8)};
      }
    }
    &[data-variant=${ButtonVariant.Blue}] {
      color: ${Colour.Clarity.Blue.Hard};
      &:hover {
        color: ${withAlpha(Colour.Clarity.Blue.Hard, 0.8)};
      }
    }
  }

  &.as-link {
    width: max-content;
    text-decoration: none;
    padding-top: 0;
    padding-bottom: 0;
  }
`;
